import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Select, Button, Space, Card } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import OperationMenu from './OperationMenu.jsx';
import OperationItem from './OperationItem.jsx';
import QueryPreview from './QueryPreview.jsx';
import MetricsExplorerModal from './MetricsExplorerModal.jsx';
import rangeData from '../locales/rangeOptions.json';
import { alertApi } from '../services/alertApi';
import { parsePromqlExpression } from '../utils/parsePromqlExpression';
import { buildGroupingLabelKeys } from '../utils/promqlLabelHelpers';

const { Option } = Select;
<style>
{`
  /* Allow selected item text to wrap/break inside the Select */
  .promql-select .ant-select-selection-item {
    white-space: normal !important;
    word-break: break-all !important;
  }

  /* Allow dropdown items to wrap */
  .promql-select .ant-select-item-option-content {
    white-space: normal !important;
    word-break: break-all !important;
  }
`}
</style>

export default function PromQLBuilder(props) {
  const [metric, setMetric] = useState('');
  const [labelFilters, setLabelFilters] = useState([{ key: '', op: '=', value: '' }]);
  const [ops, setOps] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [explorerVisible, setExplorerVisible] = useState(false);
  const [metricDropdownOpen, setMetricDropdownOpen] = useState(false);
  const lastParsedExpression = useRef(null);

  const { labelKeys: propsLabelKeys, valueOptions: propsValueOptions, isDark = false, value: initialExpression, onChange: onExpressionChange } = props;

  // When editing: parse initial expression to pre-fill metric, label filters, and operations
  useEffect(() => {
    if (!initialExpression || typeof initialExpression !== 'string' || initialExpression.trim() === '' || initialExpression === '{}') {
      lastParsedExpression.current = null;
      return;
    }
    if (lastParsedExpression.current === initialExpression) return;

    const parsed = parsePromqlExpression(initialExpression.trim());
    setMetric(parsed.metric || '');
    setLabelFilters(
      parsed.labelFilters?.length
        ? parsed.labelFilters
        : [{ key: '', op: '=', value: '' }]
    );
    setOps(parsed.ops || []);
    lastParsedExpression.current = initialExpression;
  }, [initialExpression]);

  // Derive label keys from the selected metric's series (not the entire catalog).
  const { labelKeys, valueOptions } = useMemo(() => {
    if (propsLabelKeys && propsLabelKeys.length > 0) {
      const sorted = [...propsLabelKeys].sort((a, b) => String(a).localeCompare(String(b)));
      return { labelKeys: sorted, valueOptions: propsValueOptions || {} };
    }

    const keys = new Set();
    const values = {};

    const addSeries = (series) => {
      if (!series || typeof series !== 'object') return;
      Object.keys(series).forEach((key) => {
        if (key === '__name__') return;
        keys.add(key);
        if (!values[key]) values[key] = [];
        const val = series[key];
        if (val && !values[key].includes(val)) {
          values[key].push(val);
        }
      });
    };

    if (metric) {
      const metricSeries = metrics.filter((m) => {
        const name = typeof m === 'string' ? m : m?.__name__;
        return name === metric;
      });
      metricSeries.forEach(addSeries);

      // Name-only catalog entries: sensible defaults for common Kubernetes metrics.
      if (metricSeries.length === 0 && /^kube_/.test(metric)) {
        ['namespace', 'pod', 'container', 'phase', 'uid', 'node', 'job', 'instance'].forEach((k) => keys.add(k));
      }
    }

    labelFilters.forEach((f) => {
      if (f.key) keys.add(f.key);
    });
    ops.forEach((o) => {
      (o.params?.by || []).forEach((k) => keys.add(k));
    });

    const sortedKeys = Array.from(keys).sort((a, b) => String(a).localeCompare(String(b)));
    const sortedValues = {};
    Object.keys(values).sort((a, b) => a.localeCompare(b)).forEach((k) => {
      sortedValues[k] = [...(values[k] || [])].sort((a, b) => String(a).localeCompare(String(b)));
    });
    return { labelKeys: sortedKeys, valueOptions: sortedValues };
  }, [propsLabelKeys, propsValueOptions, metrics, metric, labelFilters, ops]);

  const metricSeries = useMemo(() => {
    if (!metric) return [];
    return metrics.filter((entry) => {
      const name = typeof entry === "string" ? entry : entry?.__name__;
      return name === metric;
    });
  }, [metrics, metric]);

  const groupingLabelKeys = useMemo(
    () =>
      buildGroupingLabelKeys({
        metricSeries,
        labelFilters,
        ops,
        fallbackLabelKeys: labelKeys,
      }),
    [metricSeries, labelFilters, ops, labelKeys],
  );

  // Metric names for autocomplete
  const metricNames = useMemo(() => {
    const names = metrics.map((m) => (typeof m === 'string' ? m : m?.__name__)).filter(Boolean);
    return [...new Set(names)].sort();
  }, [metrics]);

  // Filtered options for autocomplete - show more when searching
  const filteredOptions = useMemo(() => {
    if (!searchValue) return metricNames.slice(0, 100).map((name) => ({ value: name, label: name }));
    const filtered = metricNames.filter((name) => {
      const nameLower = name.toLowerCase();
      const words = searchValue.toLowerCase().split(/\s+/).filter(w => w.length > 0);
      return words.every((word) => nameLower.includes(word));
    });
    return filtered.slice(0, 500).map((name) => ({ value: name, label: name }));
  }, [metricNames, searchValue]);

  // Fetch metrics on mount
  useEffect(() => {
    setLoading(true);
    alertApi.getMetricsConfig()
      .then((response) => {
        let metricsData = response?.data || response || [];
        if (Array.isArray(metricsData) && metricsData.length > 0 && typeof metricsData[0] === 'string') {
          metricsData = metricsData.map((metricName) => ({ __name__: metricName }));
        }
        setMetrics(Array.isArray(metricsData) ? metricsData : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load metrics:', err);
        setMetrics([]);
        setLoading(false);
      });
  }, []);

  const rangeOptions = rangeData.ranges || ['$__auto'];

  function addOp(opSpec) {
    const base = { id: opSpec.id, display: opSpec.display || opSpec.id, params: {} };
    if (opSpec.needsRange) base.params.range = rangeOptions[0];
    if (opSpec.needsLabel) base.params.by = [];
    setOps(p => [...p, base]);
  }

  function removeOp(i) { setOps(p => p.filter((_, idx) => idx !== i)); }
  function changeOpType(i, newOp) { setOps(p => p.map((op, idx) => idx === i ? { ...newOp } : op)); }
  function updateOpParam(i, field, val) { setOps(p => p.map((op, idx) => idx === i ? { ...op, params: { ...op.params, [field]: val } } : op)); }

  function addFilter() { setLabelFilters(p => [...p, { key: '', op: '=', value: '' }]); }
  function updateFilter(i, field, val) { setLabelFilters(p => p.map((f, idx) => idx === i ? { ...f, [field]: val } : f)); }
  function removeFilter(i) { setLabelFilters(p => p.filter((_, idx) => idx !== i)); }

const query = useMemo(() => {
    // Build base selector
    const labels = labelFilters.filter(f => f.key && f.value).map(f => `${f.key}${f.op}"${f.value}"`);
    const selector = `${metric || ''}${labels.length ? '{' + labels.join(',') + '}' : ''}` || '{}';
    let expr = selector;
    // NEW: Operation-specific handling
    for (const o of ops) {
        const opId = o.id;
        const params = o.params || {};
        //Aggregations with "by" clause
        if (['sum', 'avg', 'min', 'max', 'count', 'stddev', 'stdvar', 'group', 
             'count_values', 'topk', 'bottomk', 'quantile'].includes(opId)) {
            if (params.by && params.by.length > 0) {
                if (opId === 'topk' || opId === 'bottomk') {
                    expr = `${opId}(${params.k || 5}, ${expr}) by (${params.by.join(',')})`;
                } else if (opId === 'quantile') {
                    expr = `${opId}(${params.q || 0.5}, ${expr}) by (${params.by.join(',')})`;
                } else if (opId === 'count_values') {
                    expr = `${opId} by (${params.by.join(',')})("${params.label || 'value'}", ${expr})`;
                } else {
                    expr = `${opId} by (${params.by.join(',')}) (${expr})`;
                }
            } else {
                // Without by clause
                if (opId === 'topk' || opId === 'bottomk') {
                    expr = `${opId}(${params.k || 5}, ${expr})`;
                } else if (opId === 'quantile') {
                    expr = `${opId}(${params.q || 0.5}, ${expr})`;
                } else if (opId === 'count_values') {
                    expr = `${opId}("${params.label || 'value'}", ${expr})`;
                } else {
                    expr = `${opId}(${expr})`;
                }
            }
        }
        
        //Range functions - NOW INCLUDES ALL MISSING ONES
        else if (['rate', 'irate', 'increase', 'delta', 'idelta', 'changes', 'deriv', 'resets',
                  'avg_over_time', 'sum_over_time', 'min_over_time', 'max_over_time', 
                  'count_over_time', 'quantile_over_time', 'stddev_over_time', 'stdvar_over_time',
                  'last_over_time', 'present_over_time', 'absent_over_time'].includes(opId)) {
            const range = params.range || '$__auto';
            if (opId === 'quantile_over_time') {
                expr = `${opId}(${params.q || 0.5}, ${expr}[${range}])`;
            } else {
                expr = `${opId}(${expr}[${range}])`;
            }
        }
        
        //predict_linear - Special case with t parameter
        else if (opId === 'predict_linear') {
            const range = params.range || '$__auto';
            const t = params.t || 3600;
            expr = `predict_linear(${expr}[${range}], ${t})`;
        }
        
        //Histogram functions - NEW
        else if (opId === 'histogram_quantile') {
            expr = `histogram_quantile(${params.q || 0.5}, ${expr})`;
        }
        else if (['histogram_avg', 'histogram_count', 'histogram_sum', 
                  'histogram_stddev', 'histogram_stdvar'].includes(opId)) {
            expr = `${opId}(${expr})`;
        }
        else if (opId === 'histogram_fraction') {
            expr = `histogram_fraction(${params.lower || 0}, ${params.upper || 1}, ${expr})`;
        }
        
        // Clamp functions - NEW
        else if (opId === 'clamp') {
            expr = `clamp(${expr}, ${params.min || 0}, ${params.max || 1})`;
        }
        else if (opId === 'clamp_min') {
            expr = `clamp_min(${expr}, ${params.min || 0})`;
        }
        else if (opId === 'clamp_max') {
            expr = `clamp_max(${expr}, ${params.max || 1})`;
        }
        
        // Label functions - NEW
        else if (opId === 'label_replace') {
            expr = `label_replace(${expr}, "${params.dst || 'dst'}", "${params.replacement || '$1'}", "${params.src || 'src'}", "${params.regex || '.*'}")`;
        }
        else if (opId === 'label_join') {
            expr = `label_join(${expr}, "${params.dst || 'dst'}", "${params.sep || '_'}"${params.src ? `, "${params.src}"` : ''})`;
        }
        
        //Sort functions - NEW
        else if (opId === 'sort_by_label' || opId === 'sort_by_label_desc') {
            if (params.by && params.by.length > 0) {
                expr = `${opId}(${expr}, "${params.by.join('", "')}")`;
            } else {
                expr = `${opId}(${expr})`;
            }
        }
        
        //Binary operators - ENHANCED
        else if (['+', '-', '*', '/', '%', '^', '==', '!=', '>', '<', '>=', '<='].includes(opId)) {
          const operand = params.value || '1';
          expr = `${expr} ${opId} ${operand}`;
        }
        
        //Logical operators - NEW
        else if (['and', 'or', 'unless'].includes(opId)) {
            const operand = params.value || 'up';
            expr = `(${expr} ${opId} ${operand})`;
        }
        
        //Time functions - NEW
        else if (['time', 'pi'].includes(opId)) {
            expr = `${opId}()`;
        }
        else if (['year', 'month', 'day_of_month', 'day_of_week', 'day_of_year', 
                  'days_in_month', 'hour', 'minute'].includes(opId)) {
            expr = `${opId}(${expr})`;
        }
        
        // All other math/trig functions (abs, ceil, sin, cos, etc.)
        else {
            expr = `${opId}(${expr})`;
        }
    }
    return expr;
}, [metric, labelFilters, ops]);

  const normalizeExpr = useCallback((value) => {
    let s = String(value || '').replace(/\s+/g, '');
    while (s.startsWith('(') && s.endsWith(')')) {
      let depth = 0;
      let fullyWrapped = true;
      for (let i = 0; i < s.length; i += 1) {
        if (s[i] === '(') depth += 1;
        else if (s[i] === ')') {
          depth -= 1;
          if (depth === 0 && i < s.length - 1) {
            fullyWrapped = false;
            break;
          }
        }
      }
      if (!fullyWrapped) break;
      s = s.slice(1, -1);
    }
    return s;
  }, []);

  // Notify parent of query changes (skip when rebuilt query matches saved expression)
  useEffect(() => {
    if (!onExpressionChange || !query || query === '{}') return;
    if (
      initialExpression &&
      normalizeExpr(query) === normalizeExpr(initialExpression)
    ) {
      return;
    }
    // Don't overwrite a pasted/manual expression when the visual builder can't represent it
    if (
      initialExpression &&
      initialExpression.trim() &&
      !initialExpression.includes('{}') &&
      query.includes('{}')
    ) {
      return;
    }
    onExpressionChange(query);
  }, [query, onExpressionChange, initialExpression, normalizeExpr]);

  const handleMetricSelect = useCallback((value) => {
    setMetric(value);
    setSearchValue(value);
    setMetricDropdownOpen(false);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const openExplorerFromDropdown = useCallback(() => {
    setMetricDropdownOpen(false);
    setExplorerVisible(true);
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {/* Left Panel - Builder */}
      <div style={{ flex: 1 }}>
        {/* Metric Selection */}
        <Card 
          size="small" 
          title={
            <span style={{ fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>
              Metric
              {loading && <span style={{ marginLeft: 8, color: '#1890ff', fontSize: 12, fontWeight: 400 }}>(Loading...)</span>}
              {!loading && metricNames.length > 0 && <span style={{ marginLeft: 8, color: '#52c41a', fontSize: 12, fontWeight: 400 }}>({metricNames.length} available)</span>}
            </span>
          }
          style={{ marginBottom: 12, background: isDark ? '#161b22' : undefined, borderColor: isDark ? '#30363d' : undefined }}
        >
          <Select
            className="promql-select"
            open={metricDropdownOpen}
            onDropdownVisibleChange={setMetricDropdownOpen}
            value={metric || undefined}
            placeholder="Browse and filter all metrics with a fuzzy search"
            showSearch
            filterOption={false}
            onSearch={handleSearch}
            onSelect={handleMetricSelect}
            onChange={handleMetricSelect}
            allowClear
            style={{ width: '100%' }}
            size="large"
            loading={loading}
            dropdownStyle={{ minWidth: 400 }}
            dropdownRender={(menu) => (
              <div>
                <div
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 4,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<ExportOutlined />}
                    onClick={openExplorerFromDropdown}
                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  >
                    Open Metrics explorer →
                  </Button>
                </div>
                {menu}
              </div>
            )}
            notFoundContent={
              loading ? 'Loading...' :
              searchValue ? `No metrics matching "${searchValue}"` :
              'Start typing to search or open Metrics explorer to view all'
            }
          >
            {filteredOptions.map((opt) => (
              <Option key={opt.value} value={opt.value} title={opt.label}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {opt.label}
                </div>
              </Option>
            ))}
          </Select>

          {metric && (
            <div style={{ marginTop: 8, padding: 8, background: isDark ? '#0d1f0d' : '#f6ffed', borderRadius: 4, border: isDark ? '1px solid #238636' : '1px solid #b7eb8f' }}>
              <div style={{ fontSize: 11, color: '#52c41a', marginBottom: 4 }}>✓ Selected metric:</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: isDark ? '#7ee787' : '#237804' }}>
                {metric}
              </div>
            </div>
          )}
        </Card>

        {/* Label Filters */}
        <Card 
          size="small" 
          title={<span style={{ fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>Label Filters</span>}
          extra={
            <Button 
              type="dashed" 
              size="small"
              onClick={addFilter}
            >
              Add
            </Button>
          }
          style={{ marginBottom: 12, background: isDark ? '#161b22' : undefined, borderColor: isDark ? '#30363d' : undefined }}
        >
          {labelFilters.length === 0 ? (
            <div style={{ color: isDark ? '#8b949e' : '#999', textAlign: 'center', padding: 8 }}>
              No filters added
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {labelFilters.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0,width: '100%' }}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Label"
                    value={f.key || undefined}
                    onChange={(val) => updateFilter(i, 'key', val)}
                    style={{ width: 140 }}
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    className="promql-select"
                  >
                    {labelKeys.map(k => (
                      // <Option key={k} value={k}>{k}</Option>
                      <Option key={k} value={k} title={k}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {k}
                        </div>
</Option>
                    ))}
                  </Select>
                  
                  <Select
                    value={f.op}
                    onChange={(val) => updateFilter(i, 'op', val)}
                    style={{ width: 70 }}
                    className="promql-select"
                  >
                    <Option value="=">=</Option>
                    <Option value="!=">!=</Option>
                    <Option value="=~">=~</Option>
                    <Option value="!~">!~</Option>
                  </Select>
                  
                  <Select
                    showSearch
                    allowClear
                    placeholder="Value"
                    value={f.value || undefined}
                    onChange={(val) => updateFilter(i, 'value', val)}
                    style={{
                    width: 400,             
                    overflow: 'hidden',    
                  }}
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    className="promql-select"
                  >
                    {(valueOptions[f.key] || []).map(v => (
                      // <Option key={v} value={v}>{v}</Option>
                      <Option key={v} value={v} title={v}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {v}
                        </div>
                      </Option>
                    ))}
                  </Select>
                  
                  <Button
                    type="text"
                    danger
                    onClick={() => removeFilter(i)}
                  />
                </div>
              ))}
            </Space>
          )}
        </Card>

        {/* Operations */}
        <Card 
          size="small" 
          title={<span style={{ fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>Operations</span>}
          extra={<OperationMenu mode='promql' onAdd={addOp} isDark={isDark} />}
          style={{ marginBottom: 24, background: isDark ? '#161b22' : undefined, borderColor: isDark ? '#30363d' : undefined }} 
        >
          {ops.length === 0 ? (
            <div style={{ color: isDark ? '#8b949e' : '#999', textAlign: 'center', padding: 8 }}>
              No operations added — use +Operations
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {ops.map((op, i) => (
                <OperationItem 
                  key={i} 
                  mode='promql' 
                  op={op} 
                  index={i} 
                  onRemove={removeOp} 
                  onChangeOpType={changeOpType} 
                  onUpdateParam={updateOpParam} 
                  labelKeys={labelKeys}
                  groupingLabelKeys={groupingLabelKeys}
                  rangeOptions={rangeOptions}
                />
              ))}
            </Space>
          )}
        </Card>
      </div>

      {/* Right Panel - Query Preview (editable so user can paste custom PromQL) */}
      <div style={{ width: '100%' }}>
        <QueryPreview
          value={initialExpression || query}
          onChange={onExpressionChange}
          placeholder="Paste or type PromQL (e.g. rate(http_requests_total[5m]))"
        />
      </div>

      <MetricsExplorerModal
        visible={explorerVisible}
        onClose={() => setExplorerVisible(false)}
        metrics={metrics}
        loading={loading}
        onSelect={(name) => {
          setMetric(name);
          setSearchValue(name);
        }}
      />
    </div>
  );
}
